import {
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
  isNotFoundError,
  isSuccess,
} from '@/context/services/type';
import type { FetchInvoiceReportsArgs } from '@/features/invoice_reports/types';
import { isCommonError, isObjectRecord } from '@/libs/typeguard/isError';
import { useMutation } from '@tanstack/react-query';
import { useSearchInvoiceReportsHook } from 'ap-openapi';
import type { SearchInvoiceReportsCsvEncode } from 'ap-openapi';

export { SearchInvoiceReportsCsvEncode } from 'ap-openapi';

export type ExportInvoiceReportsArgs = FetchInvoiceReportsArgs & {
  csv_encode?: SearchInvoiceReportsCsvEncode;
};

type BlobErrorPayload = { messages?: string[]; code?: string };

async function extractBlobError(data: unknown): Promise<BlobErrorPayload> {
  if (!(data instanceof Blob)) return {};
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (isObjectRecord(parsed) && isCommonError(parsed)) {
      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : undefined,
        code: typeof parsed.code === 'string' ? parsed.code : undefined,
      };
    }
  } catch {
    // non-JSON blob (e.g. actual CSV data)
  }
  return {};
}

export const EXPORT_ERROR_SENTINELS = new Set([
  'unauthorized',
  'forbidden',
  'not_found',
  'export_failed',
]);

const BLOB_REQUEST_OPTIONS = {
  headers: { Accept: 'text/csv' },
  responseType: 'blob',
} as const;

export function useExportInvoiceReportsMutation() {
  const searchInvoiceReports = useSearchInvoiceReportsHook();

  return useMutation({
    mutationFn: async (args: ExportInvoiceReportsArgs) => {
      const { page = 1, perPage = 50, query, order_by, csv_encode } = args;
      const response = await searchInvoiceReports(
        {
          pagination: { per_page: perPage, page },
          query,
          order_by,
        },
        { csv_encode },
        BLOB_REQUEST_OPTIONS
      );

      if (isSuccess(response.status) && response.data instanceof Blob) {
        return response.data;
      }

      if (isAuthError(response.status)) {
        location.reload();
        throw new Error('unauthorized');
      }

      if (isForbiddenError(response.status)) {
        const { messages, code } = await extractBlobError(response.data);
        if (code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          throw new Error(messages?.join('\n') || 'forbidden');
        }
        // check message available and show to user
        if (messages?.length) {
          throw new Error(messages.join('\n'));
        } else {
          location.reload();
        }
      }

      if (isNotFoundError(response.status)) {
        location.reload();
        throw new Error('not_found');
      }

      const { messages } = await extractBlobError(response.data);
      throw new Error(messages?.join('\n') || 'export_failed');
    },
  });
}
